import { api } from "@/lib/api";
import { appPlatform } from "@/lib/platform";

// RevenueCat (Apple IAP on iOS, Google Play Billing on Android). Web-safe: every call is a
// no-op on the web, the plugin is loaded lazily and the public SDK key comes from
// /config/public.payments.iap (managed in Admin › Intégrations, never in the code).
let mod = null;
let configuredFor = null;

const load = async () => {
  if (appPlatform() === "web") return null;
  if (!mod) mod = await import("@revenuecat/purchases-capacitor");
  return mod;
};

export const storePublicKey = (payments) => {
  const p = appPlatform();
  if (p === "ios") return (payments?.iap?.apple_public_key || "").trim();
  if (p === "android") return (payments?.iap?.google_public_key || "").trim();
  return "";
};

export const configureIap = async (payments, userId) => {
  const key = storePublicKey(payments);
  if (!key || !userId) return false;
  const m = await load();
  if (!m) return false;
  const sig = `${key}:${userId}`;
  if (configuredFor !== sig) {
    await m.Purchases.configure({ apiKey: key, appUserID: String(userId) });
    configuredFor = sig;
  }
  return true;
};

// Store product identifier for a Yalola product (kind: verified_yearly | adcredit).
export const storeProductId = (payments, kind, amount) => {
  const prod = (payments?.iap?.products || []).find((x) => x.kind === kind && (kind !== "adcredit" || Number(x.amount) === Number(amount)));
  if (!prod) return null;
  return appPlatform() === "android" ? (prod.google_product_id || prod.product_id) : prod.product_id;
};

// RevenueCat PURCHASES_ERROR_CODE (string enum) → what to tell the member. Anything unknown → generic.
const RC_ERR = {
  "1": null, // cancelled
  "2": ["iap.err_store_problem", "La boutique n'a pas pu traiter l'achat (App Store / Google Play). Réessaie dans un instant."],
  "3": ["iap.err_not_allowed", "Les achats ne sont pas autorisés sur cet appareil (restrictions ou contrôle parental)."],
  "5": ["iap.err_product_missing", "Cet achat n'est pas encore disponible dans la boutique. Réessaie plus tard."],
  "6": ["iap.err_already_owned", "Ce compte Apple / Google a déjà un abonnement Yalola Vérifié actif, rattaché à un autre compte Yalola."],
  "7": ["iap.err_receipt_in_use", "Cet abonnement est déjà utilisé par un autre compte Yalola sur ce téléphone."],
  "10": ["iap.err_network", "Connexion impossible à la boutique. Vérifie ta connexion puis réessaie."],
  "20": ["iap.err_pending", "Paiement en attente de confirmation par la boutique. Tes achats apparaîtront dès sa validation."],
  "23": ["iap.err_product_missing", "Cet achat n'est pas encore disponible dans la boutique. Réessaie plus tard."],
  "32": ["iap.err_network", "Connexion impossible à la boutique. Vérifie ta connexion puis réessaie."],
  "35": ["iap.err_network", "Connexion impossible à la boutique. Vérifie ta connexion puis réessaie."],
};
// Erreurs côté boutique où une app ancienne (APK hors Play, build retiré du store) est la 1re piste → conseil « mettre à jour ».
export const OUTDATED_HINT_CODES = ["2", "5", "23", "32"];
export const isStoreSideError = (e) => OUTDATED_HINT_CODES.includes(String(e?.code ?? ""));

const LOCAL_ERR = {
  product_missing: RC_ERR["5"],
  store_unavailable: ["iap.err_store_unavailable", "Boutique indisponible sur cet appareil. Réessaie depuis l'application Yalola à jour."],
};

// « Déjà abonné » : le compte Apple / Google détient déjà l'abonnement (PRODUCT_ALREADY_PURCHASED) ou le reçu appartient à un
// autre compte Yalola (RECEIPT_ALREADY_IN_USE, réglage RevenueCat « Keep with original App User ID ») → pas une panne,
// une explication (un abonnement = un seul compte Yalola).
export const isAlreadyOwnedError = (e) => ["6", "7"].includes(String(e?.code ?? "")) || /ALREADY_(PURCHASED|IN_USE)/i.test(String(e?.userInfo?.readableErrorCode || e?.readableErrorCode || ""));

// Human message for a failed store purchase (t = i18n from useApp).
export const storeErrorMessage = (e, t) => {
  const pair = LOCAL_ERR[e?.message] || RC_ERR[String(e?.code ?? "")] || ["iap.err_generic", "Achat impossible pour le moment. Réessaie dans un instant."];
  return t(pair[0], pair[1]);
};

// Every store attempt (ok or not) is reported to the server → Admin › Intégrations › RevenueCat « Diagnostic des achats »
// (the only way to see WHY a purchase failed on a reviewer's device: RevenueCat code + underlying store message).
const report = async (payload) => {
  try {
    let app_version = "";
    try { const { getAppVersion } = await import("@/lib/appVersion"); const v = await getAppVersion(); app_version = `${v.version} (${v.build})`; } catch { /* web */ }
    const { data } = await api.post("/iap/diagnostic", { platform: appPlatform(), app_version, ...payload });
    return data?.id || null;
  } catch { return null; /* never block the purchase flow */ }
};
// Failure path: the diagnostic is awaited so the thrown error carries `diagnosticId` (→ PurchaseFailedCard reference).
const fail = async (payload, err) => {
  const e = err instanceof Error ? err : Object.assign(new Error(payload.code || "store_error"), err || {});
  e.diagnosticId = await report(payload);
  throw e;
};
export const markPurchaseFailureInformed = (diagnosticId) => api.post(`/iap/diagnostic/${diagnosticId}/informed`).catch(() => {});
const errInfo = (e) => ({ code: e?.code != null ? String(e.code) : (e?.message || "unknown"), readable: e?.userInfo?.readableErrorCode || e?.readableErrorCode || "",
  message: [e?.message, e?.underlyingErrorMessage].filter(Boolean).join(" — ").slice(0, 400) });

// Lot 732 — synchro serveur : délai d'attente explicite (> 12 s du GET RevenueCat côté serveur) + 1 nouvelle tentative
// après une micro-coupure, et la VRAIE raison de l'échec remontée (statut HTTP / réseau / délai, durée) au lieu d'un
// « unreachable » muet (les logs de production montraient 100 % de 200 OK : l'échec était toujours côté téléphone).
export const SYNC_TIMEOUT_MS = 20000;
export const syncRetry = { delayMs: 2500 };  // objet (et non constante) : les tests raccourcissent l'attente
export const syncFailureDetail = (err, { attempts, elapsedMs }) => {
  const status = err?.response?.status;
  const code = err?.code || "";
  const what = status
    ? `HTTP ${status}${err?.response?.data?.detail ? ` « ${String(typeof err.response.data.detail === "string" ? err.response.data.detail : JSON.stringify(err.response.data.detail)).slice(0, 120)} »` : ""}`
    : code === "ECONNABORTED" || /timeout/i.test(err?.message || "") ? `délai dépassé (${SYNC_TIMEOUT_MS} ms)`
    : `réseau : ${code || err?.message || "sans réponse"}`;
  return { status: status || null, code: code || null, detail: `${what} · ${attempts} essai(s) · ${elapsedMs} ms`.slice(0, 200) };
};
const syncServer = async () => {
  const t0 = Date.now();
  let lastErr = null;
  for (let attempt = 1; attempt <= 2; attempt++) {
    try { const { data } = await api.post("/iap/sync", null, { timeout: SYNC_TIMEOUT_MS }); return { data }; }
    catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      if (status && status < 500) break;  // réponse reçue (401, 404…) : réessayer ne changera rien
      if (attempt === 1) await new Promise((r) => setTimeout(r, syncRetry.delayMs));
    }
  }
  return { error: syncFailureDetail(lastErr, { attempts: lastErr?.response?.status && lastErr.response.status < 500 ? 1 : 2, elapsedMs: Date.now() - t0 }) };
};

// Buy in the store of the device, then let the server reconcile (webhook stays the truth).
export const buyStoreProduct = async ({ payments, userId, kind, amount }) => {
  const m = await load();
  const productId = storeProductId(payments, kind, amount);
  if (!m || !(await configureIap(payments, userId))) {
    await fail({ step: "configure", kind, product_id: productId, ok: false, code: "store_unavailable", message: !m ? "plugin unavailable" : "missing store key or user" }, new Error("store_unavailable"));
  }
  if (!productId) {
    await fail({ step: "product", kind, product_id: null, ok: false, code: "product_missing", message: "no product configured in Admin › Intégrations for this kind/amount" }, new Error("product_missing"));
  }
  const type = kind === "adcredit" ? m.PRODUCT_CATEGORY?.NON_SUBSCRIPTION : m.PRODUCT_CATEGORY?.SUBSCRIPTION;
  let product;
  try {
    const { products } = await m.Purchases.getProducts({ productIdentifiers: [productId], ...(type ? { type } : {}) });
    product = (products || [])[0];
  } catch (e) {
    await fail({ step: "getProducts", kind, product_id: productId, ok: false, ...errInfo(e) }, e);
  }
  if (!product) {
    await fail({ step: "getProducts", kind, product_id: productId, ok: false, code: "product_missing", message: "store returned no product for this identifier (not created / not approved / agreement not signed)" }, new Error("product_missing"));
  }
  try {
    await m.Purchases.purchaseStoreProduct({ product });
  } catch (e) {
    if (e?.userCancelled || String(e?.code) === "1") return { cancelled: true };
    await fail({ step: "purchase", kind, product_id: productId, ok: false, ...errInfo(e) }, e);
  }
  const { data: sync, error: syncErr } = await syncServer();
  report({ step: "purchase", kind, product_id: productId, ok: true, code: "", message: sync ? "synced" : `sync pending (webhook) — ${syncErr?.detail || ""}` });
  return { cancelled: false, sync };
};

// Silent restore on every native session (new iPhone / Android, reinstall): RevenueCat customer info for this
// Yalola account (no Apple ID prompt — never restorePurchases() here) then the server mirrors the state (/iap/sync).
// Once per member and per device every 24 h. The manual « Restaurer mes achats » stays for purchases made under another account.
const AUTO_SYNC_KEY = "yalola.iap_autosync";
const AUTO_SYNC_MS = 24 * 60 * 60 * 1000;
const autoSyncStamp = (userId, write) => {
  let map = {};
  try { map = JSON.parse(localStorage.getItem(AUTO_SYNC_KEY) || "{}") || {}; } catch { map = {}; }
  const last = Number(map[userId] || 0);
  if (write) { map[userId] = Date.now(); try { localStorage.setItem(AUTO_SYNC_KEY, JSON.stringify(map)); } catch { /* quota */ } }
  return last;
};
export const autoSyncDue = (userId) => !!userId && Date.now() - autoSyncStamp(userId, false) >= AUTO_SYNC_MS;
export const autoSyncStore = async ({ payments, userId }) => {
  if (appPlatform() === "web" || !userId || !storePublicKey(payments) || !autoSyncDue(userId)) return null;
  autoSyncStamp(userId, true);
  try {
    const m = await load();
    if (!m || !(await configureIap(payments, userId))) return null;
    const { customerInfo } = await m.Purchases.getCustomerInfo();
    const entitlements = Object.keys(customerInfo?.entitlements?.active || {}).length;
    const nonSubs = (customerInfo?.nonSubscriptionTransactions || []).length;
    const { data: sync, error: syncErr } = await syncServer();
    if (!sync) report({ step: "auto_sync", kind: "", product_id: null, ok: false, code: "sync_failed", readable: syncErr?.status ? `HTTP_${syncErr.status}` : (syncErr?.code || "NETWORK"),
      message: `entitlements=${entitlements} nonsubs=${nonSubs} — /iap/sync : ${syncErr?.detail || "sans réponse"}` });
    return { sync, entitlements, nonSubs };
  } catch (e) {
    report({ step: "auto_sync", kind: "", product_id: null, ok: false, ...errInfo(e) });
    return null;
  }
};

export const restoreStorePurchases = async ({ payments, userId }) => {
  const m = await load();
  if (!m || !(await configureIap(payments, userId))) return null;
  await m.Purchases.restorePurchases();
  return (await syncServer()).data || null;
};

// Store page where the member manages (cancels) the subscription.
export const storeManagementUrl = async () => {
  const m = await load();
  if (m) {
    try {
      const { customerInfo } = await m.Purchases.getCustomerInfo();
      if (customerInfo?.managementURL) return customerInfo.managementURL;
    } catch { /* fall through */ }
  }
  const p = appPlatform();
  if (p === "ios") return "https://apps.apple.com/account/subscriptions";
  if (p === "android") return "https://play.google.com/store/account/subscriptions";
  return null;
};
